import { Skeleton } from "@mui/material";
import { useAtomValue } from "jotai";
import { useEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { CeDropdown } from "../generic/components/form-fields/ce-dropdown";
import { multiformatAtom } from "../multiformat/store/multiformat-atom";
import { useBrandToneOptions } from "./hooks/use-brand-tone-options";

import type { CreateContentRequest } from "../multiformat/store/types";
import type { FC } from "react";

export const ContentToneSelect: FC = () => {
  const { loading, error, data: brandToneOptions } = useBrandToneOptions();
  const multiformatLoader = useAtomValue(multiformatAtom);
  const previousContentTypeRef = useRef<string | null>(null);
  const hasInitializedRef = useRef(false);

  const { watch, setValue } = useFormContext<CreateContentRequest>();

  const tone = watch("tone");
  const format = watch("format");

  useEffect(() => {
    if (!multiformatLoader.data || !brandToneOptions?.length) {
      return;
    }

    const selectedContentType = multiformatLoader.data.content_types.find((c) => c.name === format);
    const currentContentType = selectedContentType?.type;
    const previousContentType = previousContentTypeRef.current;

    // Set default tone on initial load for Communication type
    if (
      !hasInitializedRef.current &&
      currentContentType === "Communication" &&
      tone === undefined
    ) {
      setValue("tone", brandToneOptions?.find((x) => x.default)?.value);
      hasInitializedRef.current = true;
      previousContentTypeRef.current = currentContentType || null;
      return;
    }

    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      previousContentTypeRef.current = currentContentType || null;
      return;
    }

    // Switching TO Communication from another type - set default tone
    if (
      currentContentType === "Communication" &&
      previousContentType !== "Communication" &&
      tone === undefined
    ) {
      setValue("tone", brandToneOptions?.find((x) => x.default)?.value);
    } else if (currentContentType !== "Communication") {
      // Reset tone only when switching FROM communication TO non-communication type
      if (tone !== undefined && previousContentType === "Communication") {
        setValue("tone", undefined);
      }
    }

    // Update the previous content type for next comparison
    previousContentTypeRef.current = currentContentType || null;
  }, [multiformatLoader.data, brandToneOptions, format, tone, setValue]);

  if (loading) {
    <Skeleton height="60px" />;
  }

  if (error) {
    return null;
  }

  return (
    <CeDropdown<CreateContentRequest>
      label="Content tone"
      analyticsCategory="mfai"
      name="tone"
      loading={loading}
      options={brandToneOptions ?? []}
      onChange={(value) => {
        if (value === null) {
          setValue("tone", undefined);
        }
      }}
      InputProps={{
        sx: {
          backgroundColor: "white",
          borderRadius: 2,
        },
      }}
    />
  );
};
